import { isNil } from 'lodash-es';

import type {
  Metadata,
  QueryResult,
  RangedFieldTypes,
} from '@dynatrace-sdk/client-query';
import { units } from '@dynatrace-sdk/units';

import {
  getFieldsArrayForRecordIndex,
  getTypesForRecordIndex,
} from './getFieldsArrayForRecordIndex.js';
import { createTimeseriesFromRecord } from './timeseries/create-timeseries-from-record.js';
import { extractStartEndDates } from './timeseries/extract-start-end-dates.js';
import {
  type Dimension,
  getDimensions,
  getValueDimensions,
} from './timeseries/get-dimensions.js';
import { mapMetricUnitToSdk } from './unit-mapping.js';
import { _getMetadataMetricsUnitForFieldName } from './unit-utils.js';
import { extractValue } from './utils/extractValue.js';
import type { Timeseries } from '../charts/core/types/timeseries.js';

/**
 * @public
 */
export interface TimeseriesWithDimensions extends Timeseries {
  userData: {
    dimensions: Dimension[];
    valueDimension: string;
  };
}

/**
 * @internal
 */
export function createName(dimensions: Array<Dimension>, valueName: string) {
  const name = [
    ...dimensions
      .map((d) =>
        typeof d.value === 'object'
          ? JSON.stringify(d.value)
          : d.value?.toString(),
      )
      .filter((x): x is string => !isNil(x)),
    valueName,
  ];

  return name;
}

function createKeyFromTimeseries(name: string | string[]): string {
  if (typeof name === 'string') {
    return name;
  }
  const dimensionKeys = [...name];
  const last = dimensionKeys.pop();
  const baseKey = dimensionKeys.join('-');
  return baseKey ? `${baseKey}_${last}` : last ?? '';
}

/**
 * Converts Grail records to Timeseries that can be used for charting
 * @param queryResult - The result of the query
 * @returns the computed Timeseries
 * @throws error if the data is malformed

* @public
*/
export function convertQueryResultToTimeseries(
  queryResult: QueryResult,
): TimeseriesWithDimensions[] {
  if (queryResult.records === null) {
    return [];
  }

  return convertToTimeseries(
    queryResult.records,
    queryResult.types,
    undefined,
    queryResult.metadata,
  );
}

/**
 * Converts Grail records to Timeseries that can be used for charting
 * @param records - The input records from the query
 * @param fieldTypes - Types of fields
 * @param hiddenFieldIds - The list of field ids which should not be converted
 * @returns the computed Timeseries
 * @throws error if the data is malformed
 * @public
 */
export function convertToTimeseries(
  records: QueryResult['records'],
  fieldTypes: RangedFieldTypes[],
  hiddenFieldIds?: string[],
  metadata?: Metadata,
): TimeseriesWithDimensions[] {
  const timeseriesMap = new Map<string, TimeseriesWithDimensions>();
  const valueDimensionSet = new Set<string>();

  for (let recordIndex = 0; recordIndex < records.length; recordIndex++) {
    const record = records[recordIndex];
    if (record === null) {
      continue;
    }

    const types = getTypesForRecordIndex(fieldTypes, recordIndex);
    if (!types || !record) {
      break;
    }
    const timeseriesArray = createTimeseriesFromRecord(record, types);
    if (timeseriesArray.length > 0) {
      timeseriesArray.forEach((timeseries) => {
        const valueDimension = timeseries.userData.valueDimension;
        valueDimensionSet.add(valueDimension);
        if (!hiddenFieldIds?.includes(valueDimension)) {
          const unit = _getMetadataMetricsUnitForFieldName(
            metadata,
            valueDimension,
          );

          const newTimeseries = {
            ...timeseries,
            unit: unit !== undefined ? mapMetricUnitToSdk(unit) : undefined,
          };

          const mapKey = createKeyFromTimeseries(timeseries.name);
          const uniqueMapKeyAcrossRecords = JSON.stringify([
            mapKey,
            recordIndex,
          ]);
          timeseriesMap.set(uniqueMapKeyAcrossRecords, newTimeseries);
        }
      });
      continue;
    }
    const fieldsArray = getFieldsArrayForRecordIndex(fieldTypes, recordIndex);

    let dimensions = getDimensions(record, fieldsArray, false);

    if (hiddenFieldIds && hiddenFieldIds.length) {
      dimensions = dimensions.filter((dimension) => {
        return !hiddenFieldIds.includes(dimension.field.name);
      });
    }
    const [start, end] = extractStartEndDates(record, fieldsArray);

    const valueDimensions = getValueDimensions(fieldsArray, false);

    for (const valueDimension of valueDimensions ?? []) {
      const name = createName(dimensions, valueDimension.name);
      valueDimensionSet.add(valueDimension.name);

      if (name.length === 0) {
        throw new Error(
          `Could not calculate key for record ${JSON.stringify(record)}`,
        );
      }

      if (hiddenFieldIds?.includes(valueDimension.name)) {
        continue;
      }

      const timeseries = ensureMapEntry(
        timeseriesMap,
        name,
        dimensions,
        valueDimension.name,
      );
      const rawValue = extractValue<number | string | undefined>(
        record,
        valueDimension.name,
      );
      const value =
        typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue;

      if (valueDimension.type === 'duration') {
        timeseries.unit = units.time.nanosecond;
      }

      if (value !== undefined) {
        timeseries.datapoints.push({ start, end, value });
      }
    }
  }

  const timeseries = Array.from(timeseriesMap.values()).filter(
    (timeseries) => timeseries.datapoints.length > 0,
  );
  timeseries.forEach((t) => {
    t.datapoints.sort((a, b) => a.start.getTime() - b.start.getTime());

    // remove value dimension from name if there is just one value dimension
    if (
      valueDimensionSet.size === 1 &&
      Array.isArray(t.name) &&
      t.name.length > 1
    ) {
      t.name.pop();
    }
  });

  return timeseries;
}

/** Ensures map entry */
function ensureMapEntry(
  timeseriesMap: Map<string, TimeseriesWithDimensions>,
  name: string | string[],
  dimensions: Dimension[],
  valueDimension: string,
): TimeseriesWithDimensions {
  const key = createKeyFromTimeseries(name);
  if (key !== undefined && key !== null && !timeseriesMap.has(key)) {
    const timeseries: TimeseriesWithDimensions = {
      datapoints: [],
      name,
      userData: {
        dimensions,
        valueDimension,
      },
    };
    timeseriesMap.set(key, timeseries);
    return timeseries;
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return timeseriesMap.get(key)!;
}
